import React, { useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../../layouts/PageTitle";
import { selectActiveProjectId } from "../../../../store/projectSlice";
import {
  appApi,
  useGetNetplusModemEntriesQuery,
  useGetNetplusModemSummaryQuery,
} from "../../../../store/appApi";
import { deleteNetplusModemEntryApi } from "../../../../services/netplusModem";
import EntryModal from "./EntryModal";
import {
  NETPLUS_MODEM_PROJECT_ID,
  BUCKETS,
  bucketTotal,
  formatDateLabel,
  monthValue,
  monthLabel,
  toDateInputValue,
} from "./config";

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const fieldClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";
const labelClass =
  "mb-1 block text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500";

const triple = (v = {}) => BUCKETS.map((b) => Number(v?.[b.key]) || 0);

// A balance / movement cell: prominent total + faint per-bucket breakdown.
const Cell = ({ value, tone = "plain", empty = false }) => {
  if (empty) {
    return <td className="px-4 py-3 text-center text-gray-300">—</td>;
  }
  const total = bucketTotal(value);
  const toneCls =
    tone === "neg"
      ? "text-rose-600"
      : tone === "pos"
        ? "text-emerald-600"
        : tone === "net"
          ? total > 0
            ? "text-rose-600"
            : total < 0
              ? "text-emerald-600"
              : "text-gray-400"
          : "text-gray-900";
  const prefix =
    tone === "neg" && total > 0
      ? "−"
      : tone === "pos" && total > 0
        ? "+"
        : tone === "net" && total > 0
          ? "+"
          : "";
  return (
    <td className="px-4 py-3 text-center">
      <div className={`text-[15px] font-semibold tabular-nums ${toneCls}`}>
        {prefix}
        {Math.abs(total) === total ? total : total}
      </div>
      <div className="mt-0.5 flex justify-center gap-1.5 text-[10px] font-medium text-gray-400">
        {triple(value).map((n, i) => (
          <span key={BUCKETS[i].key}>
            {BUCKETS[i].label[0]}&nbsp;{n}
          </span>
        ))}
      </div>
    </td>
  );
};

const NetplusModemList = () => {
  const dispatch = useDispatch();
  const activeProjectId = useSelector(selectActiveProjectId);
  const allowed = activeProjectId === NETPLUS_MODEM_PROJECT_ID;

  const [filterMode, setFilterMode] = useState("month");
  const [month, setMonth] = useState(monthValue());
  const [day, setDay] = useState(toDateInputValue());
  const [modal, setModal] = useState({ show: false, entry: null });

  const queryArg = filterMode === "month" ? { month } : { date: day };
  const { data, isLoading, isFetching } = useGetNetplusModemEntriesQuery(
    queryArg,
    { skip: !allowed },
  );
  const { data: summaryData } = useGetNetplusModemSummaryQuery(undefined, {
    skip: !allowed,
  });

  const entries = data?.entries || [];
  const monthTotals = data?.monthTotals || null;
  const latest = data?.latest || null;
  const summary = summaryData?.summary || {};
  const hasOpening = Boolean(summary.hasOpening);
  const closing = summary.closing || {};

  const rows = useMemo(
    () => [...entries].sort((a, b) => new Date(b.date) - new Date(a.date)),
    [entries],
  );

  const openAdd = () => setModal({ show: true, entry: null });
  const openEdit = (entry) => setModal({ show: true, entry });
  const closeModal = () => setModal({ show: false, entry: null });

  const handleDelete = async (entry) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Delete this entry?",
      html: `<div class="small text-muted">${formatDateLabel(
        entry.date,
      )} — every later day will be re-balanced automatically.</div>`,
      showCancelButton: true,
      confirmButtonColor: "#e11d48",
      confirmButtonText: "Delete",
    });
    if (!res.isConfirmed) return;
    try {
      await deleteNetplusModemEntryApi(entry._id);
      dispatch(appApi.util.invalidateTags(["NetplusModem"]));
      Swal.fire({
        icon: "success",
        title: "Deleted",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not delete",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    }
  };

  if (!allowed) {
    return (
      <>
        <PageTitle activeMenu="Modem Recovery & Recharges" motherMenu="Netplus" />
        <div className={`${panelClass} p-10 text-center`}>
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
            <i className="fa-solid fa-router text-xl" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">
            Not available for this project
          </h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">
            Netplus Modem Recovery &amp; Recharges is enabled only for the{" "}
            <strong>Satti &amp; Co</strong> project. Switch the active project to
            open it.
          </p>
        </div>
      </>
    );
  }

  const kpis = [
    ...BUCKETS.map((b) => ({
      label: b.label,
      value: closing[b.key] ?? 0,
      accent: "text-gray-900",
    })),
    {
      label: "Non-active pool",
      value: summary.closingTotal ?? 0,
      accent: "text-amber-600",
    },
  ];

  return (
    <>
      <PageTitle activeMenu="Modem Recovery & Recharges" motherMenu="Netplus" />

      <div className="space-y-6">
        {/* ── Hero ── */}
        <section className={`${panelClass} relative overflow-hidden p-6`}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(169,126,8,0.10),_transparent_35%),radial-gradient(circle_at_bottom_left,_rgba(226,232,240,0.6),_transparent_40%)]" />
          <div className="relative flex flex-wrap items-start justify-between gap-6">
            <div className="min-w-[260px]">
              <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                Netplus · Satti &amp; Co
              </span>
              <h1 className="mt-4 text-3xl font-semibold text-gray-900 md:text-4xl">
                Modem Recovery &amp; Recharges
              </h1>
              <p className="mt-2 max-w-xl text-sm text-gray-500">
                Daily running count of subscriber modems in the Inactive,
                Suspended and Terminated buckets. Each day carries forward from
                the previous close.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={openAdd}
                  className="rounded-full bg-amber-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400"
                >
                  {hasOpening ? "+ Add Daily Entry" : "+ Add Opening Entry"}
                </button>
                {summary.latestDate && (
                  <span className="inline-flex items-center rounded-full border border-gray-200 px-4 py-2 text-xs font-medium text-gray-500">
                    Last updated {formatDateLabel(summary.latestDate)}
                  </span>
                )}
              </div>
            </div>

            <div className="grid min-w-[320px] flex-1 grid-cols-2 gap-3 sm:grid-cols-4">
              {kpis.map((k) => (
                <div
                  key={k.label}
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3 text-center"
                >
                  <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                    {k.label}
                  </div>
                  <div
                    className={`mt-1 text-2xl font-semibold tabular-nums ${k.accent}`}
                  >
                    {k.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Ledger ── */}
        <section className={panelClass}>
          <div className="flex flex-wrap items-end justify-between gap-4 border-b border-gray-200 px-5 py-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Daily Ledger
              </h2>
              <p className="mt-0.5 text-xs text-gray-500">
                {filterMode === "month"
                  ? monthLabel(month)
                  : formatDateLabel(day)}
              </p>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div>
                <span className={labelClass}>View</span>
                <div className="inline-flex rounded-xl border border-gray-300 p-0.5">
                  {["month", "day"].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setFilterMode(m)}
                      className={`rounded-lg px-4 py-1.5 text-xs font-semibold capitalize transition ${
                        filterMode === m
                          ? "bg-amber-500 text-slate-950"
                          : "text-gray-500 hover:text-gray-800"
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <span className={labelClass}>
                  {filterMode === "month" ? "Month" : "Date"}
                </span>
                {filterMode === "month" ? (
                  <input
                    type="month"
                    className={`${fieldClass} w-[170px]`}
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  />
                ) : (
                  <input
                    type="date"
                    className={`${fieldClass} w-[170px]`}
                    value={day}
                    onChange={(e) => setDay(e.target.value)}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-gray-500">
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                    Date
                  </th>
                  {["Opening", "Recovered", "Recharge", "Added", "Closing", "Net"].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    ),
                  )}
                  <th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading || isFetching ? (
                  <tr>
                    <td colSpan={8} className="px-5 py-12 text-center text-gray-400">
                      Loading…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-5 py-12 text-center text-gray-400">
                      No entries for this {filterMode}.
                    </td>
                  </tr>
                ) : (
                  rows.map((e) => (
                    <tr key={e._id} className="transition hover:bg-amber-50/40">
                      <td className="px-5 py-3 align-top">
                        <div className="font-semibold text-gray-900">
                          {formatDateLabel(e.date)}
                        </div>
                        {e.isOpening && (
                          <span className="mt-1 inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                            Opening
                          </span>
                        )}
                        {e.note && (
                          <div className="mt-1 max-w-[220px] text-xs text-gray-400">
                            {e.note}
                          </div>
                        )}
                      </td>
                      <Cell value={e.opening} />
                      <Cell value={e.recovered} tone="neg" empty={e.isOpening} />
                      <Cell value={e.recharge} tone="neg" empty={e.isOpening} />
                      <Cell value={e.added} tone="pos" empty={e.isOpening} />
                      <Cell value={e.closing} />
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`text-[15px] font-bold tabular-nums ${
                            e.netValue > 0
                              ? "text-rose-600"
                              : e.netValue < 0
                                ? "text-emerald-600"
                                : "text-gray-400"
                          }`}
                        >
                          {e.netValue > 0 ? "+" : ""}
                          {e.netValue ?? 0}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right align-top">
                        <div className="inline-flex gap-1">
                          <button
                            onClick={() => openEdit(e)}
                            className="rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 transition hover:border-amber-400 hover:text-amber-700"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(e)}
                            className="rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 transition hover:border-rose-300 hover:text-rose-600"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {monthTotals && rows.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
                    <td className="px-5 py-3 text-xs uppercase tracking-wider text-gray-500">
                      Month total · {monthTotals.days} days
                    </td>
                    <Cell value={monthTotals.opening} />
                    <Cell value={monthTotals.recovered} tone="neg" />
                    <Cell value={monthTotals.recharge} tone="neg" />
                    <Cell value={monthTotals.added} tone="pos" />
                    <Cell value={monthTotals.closing} />
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`text-[15px] font-bold tabular-nums ${
                          monthTotals.netValue > 0
                            ? "text-rose-600"
                            : monthTotals.netValue < 0
                              ? "text-emerald-600"
                              : "text-gray-400"
                        }`}
                      >
                        {monthTotals.netValue > 0 ? "+" : ""}
                        {monthTotals.netValue}
                      </span>
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="border-t border-gray-100 px-5 py-3 text-[11px] text-gray-400">
            <span className="font-semibold text-rose-500">Recovered</span> and{" "}
            <span className="font-semibold text-rose-500">Recharge</span> reduce a
            bucket · <span className="font-semibold text-emerald-500">Added</span>{" "}
            increases it · Net = Added − Recovered − Recharge
          </div>
        </section>
      </div>

      <EntryModal
        show={modal.show}
        onClose={closeModal}
        entry={modal.entry}
        isFirstEver={!hasOpening}
        prevClosing={latest?.closing || {}}
        onSaved={() => {}}
      />
    </>
  );
};

export default NetplusModemList;
